import React from "react";
import { SaveIcon } from "../../../components/icons";
import { Button, type ButtonProps } from "../../../components/ui/button";
import { Alert } from "../../../components/ui/alert";
import { Spinner } from "../../../components/ui/feedback";
import { Snackbar } from "../../../components/ui/snackbar";
import { useSaveAssemblyMutation } from "../api/playbookApi";
import { getCurrentDocumentAsBlob } from "../../../platform/word/documentTools";
import { AssemblyApiContext } from "../types/assembly-context";

interface SaveAssemblyProps {
  ctx: AssemblyApiContext | null;
  disabled?: boolean;
  size?: ButtonProps["size"];
  variant?: ButtonProps["variant"];
  /** The idle label. "Save" alone is ambiguous next to the header's Save to Jylo. */
  label?: string;
}

const SaveAssemblyButton = ({
  ctx,
  disabled = false,
  size = "default",
  variant = "default",
  label = "Save",
}: SaveAssemblyProps) => {
  const [saveAssembly, { isLoading: isSaving, isError, isSuccess, reset }] =
    useSaveAssemblyMutation();

  const handleSaveAssembly = async () => {
    if (!ctx) return;

    const documentBlob = await getCurrentDocumentAsBlob();
    const documentFile = new File([documentBlob], "current-document.docx", {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    saveAssembly({ ctx, docxFile: documentFile });
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        disabled={isSaving || !ctx || disabled}
        onClick={handleSaveAssembly}
      >
        {isSaving ? <Spinner size={16} className="text-current" /> : <SaveIcon />}
        {isSaving ? "Saving…" : label}
      </Button>

      <Snackbar open={isSuccess} autoHideDuration={3000} align="end" onClose={() => reset()}>
        <Alert severity="success" variant="filled" className="min-w-[240px]">
          Saved successfully
        </Alert>
      </Snackbar>

      <Snackbar open={isError} autoHideDuration={5000} align="end" onClose={() => reset()}>
        <Alert severity="error" variant="filled" className="min-w-[240px]">
          Failed to save document. Please try again.
        </Alert>
      </Snackbar>
    </>
  );
};

export default SaveAssemblyButton;
